import { useEffect } from 'react';

import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { DefaultValues, FieldValues, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { useFlowNavigation } from './updateContextAndGoToNextStep';
import { useFlowTracking } from './useFlowTracking';
import { useSummaryStepMachines } from './useSummaryStepMachines';
import { FlowConsumerContextEnergy } from '../../context';
import { createIbanFormSchema } from '../components/SummaryStep/createIbanFormSchema';
import type { SummaryPaymentDetailsTextualData } from '../components/SummaryStep/summary-step.types';
import { getOffer, getOfferTermsAndConditions, getCashbackTexts } from '../helpers';
import { checkOnDiscountType, constructTextualDataKey } from '../helpers/sitecore';

/**
 * Which offer/order machine events this step dispatches:
 * - 'never' — always the regular new-contract events (GET_OFFER / SEND_ORDER)
 * - 'always' — always the extension events (GET_EXTENSION_OFFER / SEND_EXTENSION_ORDER)
 * - 'auto' — picks per-submission based on `flowContext.isContractExtension`
 */
type ExtensionMode = 'never' | 'always' | 'auto';

interface UseSummaryStepLogicOptions<TFormFields extends FieldValues> {
  extensionMode: ExtensionMode;
  /** Extra yup fields (e.g. retention's `energyUsagePurpose`) concatenated onto the IBAN schema. */
  extraSchemaFields?: yup.ObjectShape;
  paymentDetailsTextualData?: SummaryPaymentDetailsTextualData;
  promotionTexts?: Exclude<Parameters<typeof getCashbackTexts>[1], undefined>;
  /**
   * Called after a successful submit, before the order is sent. Return `true` to block the order
   * submission (e.g. retention's "business usage" branch) — the form is reset (keeping values) instead.
   */
  shouldBlockSubmit?: (values: TFormFields) => boolean;
  /**
   * When true (default), the success-navigation effect (tracking + advancing the flow) only runs once
   * `selectedOffer` is resolved. The plain `SummaryStep` intentionally advances on order SUCCESS even
   * without a resolved offer, so it opts out via `false`.
   */
  requireSelectedOfferForSuccess?: boolean;
  /** Whether the navigation payload includes `orderedOffer: selectedOffer`. */
  includeOrderedOfferInNavigation?: boolean;
}

export const useSummaryStepLogic = <TFormFields extends FieldValues>({
  extensionMode,
  extraSchemaFields,
  paymentDetailsTextualData,
  promotionTexts,
  shouldBlockSubmit,
  requireSelectedOfferForSuccess = true,
  includeOrderedOfferInNavigation = false,
}: UseSummaryStepLogicOptions<TFormFields>) => {
  const { useFlowSelector, useFlowActorRef } = useFlowHooks<FlowConsumerContextEnergy>();
  const flowState = useFlowSelector(state => state);
  const sendToFlowMachine = useFlowActorRef().send;
  const { updateContextAndGoToNextStep } = useFlowNavigation();
  const { trackPurchase, trackFillUsage } = useFlowTracking();

  const {
    label,
    orderState,
    sendToOrderMachine,
    offerState,
    sendToOfferMachine,
    isLoading,
    isSuccessful,
    isFailed,
    isNotFound,
    isNotAvailable,
    isNotAuthenticated,
    isOrderFailed,
  } = useSummaryStepMachines({ includeExtensionFetching: extensionMode !== 'never' });

  const { context: flowContext } = flowState;
  const { energyType, isPersonalDetailsEditProhibited } = flowContext;

  const offer = offerState?.context?.offer;
  const selectedOffer = getOffer(flowContext, offer);
  const selectedOfferTermsAndConditions = getOfferTermsAndConditions(flowContext, offer);
  const selectedCashbackTexts = getCashbackTexts(selectedOffer?.costDiscounts?.cashback?.type, promotionTexts);

  const textualDataKey = checkOnDiscountType(
    constructTextualDataKey(selectedOffer?.contractSpan, energyType),
    selectedOffer?.discountType,
  );

  const formSchema = yup
    .object(extraSchemaFields)
    .concat(createIbanFormSchema(isPersonalDetailsEditProhibited, paymentDetailsTextualData));

  const form = useForm<TFormFields>({
    mode: 'onSubmit',
    defaultValues: { iban: flowContext.iban } as unknown as DefaultValues<TFormFields>,
    resolver: typedYupResolver<TFormFields>(formSchema),
  });

  const {
    getValues,
    reset,
    formState: { isSubmitSuccessful },
  } = form;

  const resolveEventType = (regular: string, extension: string): string => {
    if (extensionMode === 'always') return extension;
    if (extensionMode === 'auto') return flowContext?.isContractExtension ? extension : regular;
    return regular;
  };

  useEffect(() => {
    sendToOfferMachine({
      type: resolveEventType('GET_OFFER', 'GET_EXTENSION_OFFER'),
      flowContext: { ...flowContext },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isSubmitSuccessful) return;

    const values = getValues();

    if (shouldBlockSubmit?.(values)) {
      reset(undefined, { keepValues: true });
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload: any = {
      type: resolveEventType('SEND_ORDER', 'SEND_EXTENSION_ORDER'),
      values: { ...flowContext, ...values },
      offers: offerState.context.offer ?? { isMkb: !!flowContext.isSME },
    };

    sendToOrderMachine(payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  useEffect(() => {
    if (!orderState.matches('SUCCESS')) return;
    if (requireSelectedOfferForSuccess && !selectedOffer) return;

    if (selectedOffer) trackPurchase(selectedOffer, offer, orderState.context.switchTypes);
    trackFillUsage({ ...flowContext });

    updateContextAndGoToNextStep(
      includeOrderedOfferInNavigation ? { ...getValues(), orderedOffer: selectedOffer } : { ...getValues() },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderState]);

  return {
    label,
    flowContext,
    sendToFlowMachine,
    offerState,
    isLoading,
    isSuccessful,
    isFailed,
    isNotFound,
    isNotAvailable,
    isNotAuthenticated,
    isOrderFailed,
    selectedOffer,
    selectedOfferTermsAndConditions,
    selectedCashbackTexts,
    textualDataKey,
    form,
  };
};
